import React from 'react';
import { useController, FieldValues, UseControllerProps } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { getInputBaseClassName } from 'components/_new/InputBase';
import CustomLabel from './Label';

interface Props<T extends FieldValues> extends UseControllerProps<T> {
	disabled?: boolean;
	placeholder?: string;
	label?: string;
	labelClassName?: string;
	underLabel?: string;
	required?: boolean;
	className?: string;
	inputClassName?: string;
	links?: string[] | null;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	maxLength?: number;
	autoComplete?: string;
	type?: string;
	readOnly?: boolean;
}

const ValidateTextField = <T extends FieldValues>(props: Props<T>) => {
	const {
		name,
		label,
		labelClassName,
		underLabel,
		placeholder,
		control,
		required = false,
		disabled = false,
		rules,
		className,
		inputClassName,
		links,
		onChange,
		maxLength,
		autoComplete,
		type = 'text',
		readOnly = false,
	} = props;
	const finalClassName = classNames(className);

	const { t } = useTranslation();

	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: { required: required ? `${label ?? placeholder} ${t('misc.forms.isRequired')}` : false, ...rules },
	});

	return (
		<div className={finalClassName}>
			{label && (
				<CustomLabel name={name} required={required} className={labelClassName}>
					<Trans
						components={
							links
								? links.map((link) => (
										// eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
										<a href={link} target="_blank" rel="noreferrer" className="p-link" />
								  ))
								: undefined
						}
					>
						{label}
					</Trans>
				</CustomLabel>
			)}
			{underLabel && <p className="mt-0 mb-2 px-3 text-sm text-muted">{underLabel}</p>}

			<input
				{...field}
				type={type}
				onChange={(event) => {
					if (onChange) {
						onChange(event);
					}
					if (field?.onChange) {
						field.onChange(event);
					}
				}}
				value={field.value && field.value !== 'undefined' ? field.value : ''}
				id={field.name}
				placeholder={placeholder}
				className={classNames(getInputBaseClassName({ disabled }), 'input-text', inputClassName, {
					'input-invalid': error,
				})}
				disabled={disabled}
				maxLength={maxLength}
				autoComplete={autoComplete}
				readOnly={readOnly}
			/>

			{error && <small className="p-error font-bold ml-3">{error.message}</small>}
		</div>
	);
};

export default ValidateTextField;
